
import { supabase, SUPABASE_URL, getSupabaseHeaders } from '@/integrations/supabase/client';
import { transformSupabasePollToPoll } from './pollDataTransformers';
import { Poll } from '@/types/poll';

/**
 * Fetches a single poll by ID from the API
 * 
 * @param pollId The unique ID of the poll to fetch
 * @returns The poll data or null if not found
 */
export const fetchPollById = async (pollId: string): Promise<Poll | null> => {
  if (!pollId) {
    console.error('No poll ID provided');
    return null;
  }
  
  try {
    const queryUrl = `${SUPABASE_URL}/rest/v1/polls?id=eq.${pollId}&select=*`;
    
    const response = await fetch(queryUrl, {
      headers: getSupabaseHeaders()
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error (${response.status}): ${errorText}`);
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const pollsData = await response.json();
    
    if (!pollsData || pollsData.length === 0) {
      console.log(`Poll with ID ${pollId} not found`);
      return null;
    }
    
    // Transform the first result
    const transformedPoll = transformSupabasePollToPoll(pollsData[0]);
    return transformedPoll;
  } catch (error) {
    console.error('Error fetching poll by ID:', error);
    throw error;
  }
};

/**
 * Fetches a single poll by poll_number from the API
 * 
 * @param pollNumber The poll_number string (e.g., "US-2025-PL-1")
 * @returns The poll data or null if not found
 */
export const fetchPollByNumber = async (pollNumber: string): Promise<Poll | null> => {
  if (!pollNumber) {
    console.error('No poll number provided');
    return null;
  }
  
  try {
    // URL encode the poll number to handle special characters
    const encodedPollNumber = encodeURIComponent(pollNumber);
    const queryUrl = `${SUPABASE_URL}/rest/v1/polls?poll_number=eq.${encodedPollNumber}&select=*`;
    
    const response = await fetch(queryUrl, {
      headers: getSupabaseHeaders()
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error (${response.status}): ${errorText}`);
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    
    const pollsData = await response.json();
    
    if (!pollsData || pollsData.length === 0) {
      console.log(`Poll with number ${pollNumber} not found`);
      return null;
    }
    
    // Transform the first result
    const transformedPoll = transformSupabasePollToPoll(pollsData[0]);
    return transformedPoll;
  } catch (error) {
    console.error('Error fetching poll by number:', error);
    throw error;
  }
};

/**
 * Fetch poll votes distribution for visualization
 * 
 * @param pollId The ID of the poll to fetch results for
 * @returns The votes data for charts
 */
export const fetchPollVotesDistribution = async (pollId: string) => {
  try {
    console.log('Fetching vote distribution for poll:', pollId);
    
    // Get accurate vote distribution using the RPC function
    const { data: voteBreakdown, error: breakdownError } = await supabase
      .rpc('get_poll_vote_breakdown', {
        poll_id_param: pollId
      });
      
    if (breakdownError) {
      console.error('Error fetching vote breakdown:', breakdownError);
      throw breakdownError;
    }
    
    console.log('Vote breakdown data:', voteBreakdown);
    
    // Get poll data to map option IDs to labels
    const { data: poll, error: pollError } = await supabase
      .from('polls')
      .select('questions')
      .eq('id', pollId)
      .single();
      
    if (pollError) {
      console.error('Error fetching poll data:', pollError);
      throw pollError;
    }
    
    // Safely access the options with proper type assertion
    const questions = poll.questions as { options?: any[], total_votes?: number };
    const options = questions.options || [];
    
    // Calculate total votes from the breakdown
    // Fix the TypeScript error by safely accessing _totals with type checking
    const totalVotes = voteBreakdown && 
                      typeof voteBreakdown === 'object' && 
                      voteBreakdown !== null && 
                      '_totals' in voteBreakdown && 
                      voteBreakdown._totals && 
                      typeof voteBreakdown._totals === 'object' &&
                      'total' in voteBreakdown._totals ? 
                      voteBreakdown._totals.total : 0;
    
    // Create the chart data by combining option information with vote counts
    const distribution = options.map((option: any) => {
      const optionId = option.id.toString();
      
      // Safely access vote counts with proper type checking
      const votes = voteBreakdown && 
                   typeof voteBreakdown === 'object' && 
                   voteBreakdown !== null && 
                   optionId in voteBreakdown && 
                   voteBreakdown[optionId] && 
                   typeof voteBreakdown[optionId] === 'object' &&
                   'total' in voteBreakdown[optionId] ? 
                   voteBreakdown[optionId].total : 0;
                   
      // Fix TypeScript error by ensuring totalVotes and votes are treated as numbers
      const percentage = typeof totalVotes === 'number' && totalVotes > 0 ? 
                         Math.round((typeof votes === 'number' ? votes : 0) * 100 / totalVotes) : 0;
      
      return {
        optionId: option.id,
        label: option.text,
        votes: votes,
        percentage: percentage
      };
    });
    
    console.log('Generated chart distribution data:', distribution);
    return distribution;
  } catch (error) {
    console.error('Error fetching poll vote distribution:', error);
    return [];
  }
};

/**
 * Fetch related insight cards for a poll
 * 
 * @param pollId The ID of the poll
 * @returns Array of related insight cards
 */
export const fetchRelatedInsightCards = async (pollId: string) => {
  try {
    // First check if this poll is linked to any cards
    const { data: links } = await supabase
      .from('poll_card_links')
      .select('card_id')
      .eq('poll_id', pollId);
      
    if (!links || links.length === 0) return [];
    
    const cardIds = links.map(link => link.card_id);
    
    // Fetch the cards by these IDs
    const { data: cards } = await supabase
      .from('insight_cards')
      .select('*')
      .in('id', cardIds)
      .eq('status', 'published');
      
    return cards || [];
  } catch (error) {
    console.error('Error fetching related insight cards:', error);
    return [];
  }
};
